import React, { useState, useEffect } from 'react';
import { Calendar } from './Calendar';
import { CalendarEvent } from '../types/calendar';
import { ArrowLeft, Plus, Calendar as CalendarIcon, MapPin, Users, Clock, X, Loader, AlertCircle, Check, Palette, Search, Filter, Download } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { format, addDays, startOfMonth, endOfMonth, isSameDay } from 'date-fns';
import { de } from 'date-fns/locale';
import { useLocalAuth } from '../hooks/useLocalAuth';
import { addAuditLog } from '../lib/auditLog';
import { EventColor, eventColors } from '../types/eventColor';

export const CalendarView: React.FC = () => {
  const navigate = useNavigate();
  const { user } = useLocalAuth();
  const [selectedEvent, setSelectedEvent] = useState<CalendarEvent | null>(null);
  const [showNewEventForm, setShowNewEventForm] = useState(false);
  const [isCreatingEvent, setIsCreatingEvent] = useState(false);
  const [events, setEvents] = useState<CalendarEvent[]>([]);
  const [filteredEvents, setFilteredEvents] = useState<CalendarEvent[]>([]);
  const [newEvent, setNewEvent] = useState({
    title: '',
    date: format(new Date(), 'yyyy-MM-dd'),
    time: format(new Date(), 'HH:mm'),
    type: 'installation' as CalendarEvent['type'],
    description: '',
    location: '',
    participants: '',
    color: 'blue' as EventColor
  });
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [showFilters, setShowFilters] = useState(false);
  const [filters, setFilters] = useState({
    type: '' as '' | CalendarEvent['type'],
    startDate: '',
    endDate: '',
    location: ''
  });

  // Load events from localStorage on component mount
  useEffect(() => {
    const storedEvents = localStorage.getItem('installtrack_calendar_events');
    if (storedEvents) {
      try {
        const parsedEvents = JSON.parse(storedEvents, (key, value) => {
          if (key === 'date') return new Date(value);
          return value;
        });
        setEvents(parsedEvents);
        setFilteredEvents(parsedEvents);
      } catch (error) {
        console.error('Error parsing stored events:', error);
      }
    } else {
      // Generate some sample events if none exist
      const sampleEvents = generateSampleEvents();
      setEvents(sampleEvents);
      setFilteredEvents(sampleEvents);
      localStorage.setItem('installtrack_calendar_events', JSON.stringify(sampleEvents));
    }
  }, []);

  // Filter events when search term or filters change
  useEffect(() => {
    let filtered = [...events];
    
    // Apply search term
    if (searchTerm) {
      const term = searchTerm.toLowerCase();
      filtered = filtered.filter(event => 
        event.title.toLowerCase().includes(term) ||
        (event.description && event.description.toLowerCase().includes(term)) ||
        (event.location && event.location.toLowerCase().includes(term))
      );
    }
    
    // Apply filters
    if (filters.type) {
      filtered = filtered.filter(event => event.type === filters.type);
    }
    
    if (filters.startDate) {
      const startDate = new Date(filters.startDate);
      filtered = filtered.filter(event => event.date >= startDate);
    }
    
    if (filters.endDate) {
      const endDate = new Date(filters.endDate);
      endDate.setHours(23, 59, 59, 999); // End of day
      filtered = filtered.filter(event => event.date <= endDate);
    }
    
    if (filters.location) {
      const location = filters.location.toLowerCase();
      filtered = filtered.filter(event => 
        event.location && event.location.toLowerCase().includes(location)
      );
    }
    
    setFilteredEvents(filtered);
  }, [events, searchTerm, filters]);

  const handleEventClick = (event: CalendarEvent) => {
    setSelectedEvent(event);
  };

  const handleNavigateToBuilding = (buildingId: string, apartmentId?: string) => {
    if (apartmentId) {
      navigate(`/building/${buildingId}?apartment=${apartmentId}`);
    } else {
      navigate(`/building/${buildingId}`);
    }
  };

  const handleCreateEvent = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsCreatingEvent(true);
    setError(null);
    setSuccess(null);

    try {
      if (!newEvent.title.trim()) {
        throw new Error('Bitte geben Sie einen Titel ein');
      }

      const eventDate = new Date(`${newEvent.date}T${newEvent.time}`);
      
      const event: CalendarEvent = {
        id: crypto.randomUUID(),
        title: newEvent.title,
        date: eventDate,
        type: newEvent.type,
        description: newEvent.description,
        location: newEvent.location,
        participants: newEvent.participants.split(',').map(p => p.trim()).filter(Boolean),
        color: newEvent.color
      };

      const updatedEvents = [...events, event];
      setEvents(updatedEvents);
      
      // Save to localStorage
      localStorage.setItem('installtrack_calendar_events', JSON.stringify(updatedEvents));
      
      // Log the event creation
      addAuditLog('create', 'calendar', `Calendar event created: ${event.title}`);

      setSuccess('Termin erfolgreich erstellt');

      // Reset form
      setNewEvent({
        title: '',
        date: format(new Date(), 'yyyy-MM-dd'),
        time: format(new Date(), 'HH:mm'),
        type: 'installation',
        description: '',
        location: '',
        participants: '',
        color: 'blue'
      });
      
      setTimeout(() => {
        setShowNewEventForm(false);
        setSuccess(null);
      }, 1500);
    } catch (error) {
      console.error('Error creating event:', error);
      setError(error instanceof Error ? error.message : 'Fehler beim Erstellen des Termins');
    } finally {
      setIsCreatingEvent(false);
    }
  };

  const handleDateSelect = (date: Date) => {
    // Pre-fill the new event form with the selected date
    setNewEvent(prev => ({
      ...prev,
      date: format(date, 'yyyy-MM-dd'),
      time: format(new Date(), 'HH:mm')
    }));
    setShowNewEventForm(true);
  };

  const handleDeleteEvent = (eventId: string) => {
    if (window.confirm('Möchten Sie diesen Termin wirklich löschen?')) {
      const updatedEvents = events.filter(event => event.id !== eventId);
      setEvents(updatedEvents);
      
      // Save to localStorage
      localStorage.setItem('installtrack_calendar_events', JSON.stringify(updatedEvents));
      
      // Log the event deletion
      addAuditLog('delete', 'calendar', `Calendar event deleted: ${selectedEvent?.title}`);
      
      // Close the event details modal
      setSelectedEvent(null);
    }
  };

  const handleExportEvents = () => {
    try {
      // Create a CSV string
      const headers = ['Titel', 'Datum', 'Uhrzeit', 'Typ', 'Ort', 'Beschreibung', 'Teilnehmer'];
      const rows = filteredEvents.map(event => [
        event.title,
        format(event.date, 'dd.MM.yyyy'),
        format(event.date, 'HH:mm'),
        event.type,
        event.location || '',
        event.description || '',
        (event.participants || []).join('; ')
      ]);
      
      const csvContent = [
        headers.join(','),
        ...rows.map(row => row.map(cell => `"${cell.replace(/"/g, '""')}"`).join(','))
      ].join('\n');
      
      // Create a blob and download
      const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `kalender_export_${format(new Date(), 'yyyy-MM-dd')}.csv`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
      
      // Log the export
      addAuditLog('export', 'calendar', `Calendar events exported to CSV`);
    } catch (error) {
      console.error('Error exporting events:', error);
      alert('Fehler beim Exportieren der Termine');
    }
  };

  const resetFilters = () => {
    setSearchTerm('');
    setFilters({
      type: '',
      startDate: '',
      endDate: '',
      location: ''
    });
  };

  // Generate sample events for demonstration
  function generateSampleEvents(): CalendarEvent[] {
    const today = new Date();
    const startOfCurrentMonth = startOfMonth(today);
    const endOfCurrentMonth = endOfMonth(today);
    
    const sampleEvents: CalendarEvent[] = [];
    
    // Add some installation events
    for (let i = 0; i < 5; i++) {
      const eventDate = new Date(startOfCurrentMonth);
      eventDate.setDate(Math.floor(Math.random() * 28) + 1); // Random day in current month
      eventDate.setHours(9 + Math.floor(Math.random() * 8), Math.floor(Math.random() * 60), 0); // Random time between 9 AM and 5 PM
      
      sampleEvents.push({
        id: `installation-${i}`,
        title: `Installation WE ${100 + i}`,
        date: eventDate,
        type: 'installation',
        description: 'Installation der Wohneinheit',
        location: 'Musterstraße 1, 12345 Berlin',
        participants: ['Max Mustermann', 'Anna Schmidt'],
        color: 'blue'
      });
    }
    
    // Add some meeting events
    for (let i = 0; i < 3; i++) {
      const eventDate = new Date(startOfCurrentMonth);
      eventDate.setDate(Math.floor(Math.random() * 28) + 1); // Random day in current month
      eventDate.setHours(10 + Math.floor(Math.random() * 6), Math.floor(Math.random() * 60), 0); // Random time between 10 AM and 4 PM
      
      sampleEvents.push({
        id: `meeting-${i}`,
        title: `Besprechung ${i + 1}`,
        date: eventDate,
        type: 'meeting',
        description: 'Projektbesprechung mit dem Team',
        location: 'Büro',
        participants: ['Max Mustermann', 'Anna Schmidt', 'John Doe'],
        color: 'green'
      });
    }
    
    // Add some other events
    for (let i = 0; i < 2; i++) {
      const eventDate = new Date(startOfCurrentMonth);
      eventDate.setDate(Math.floor(Math.random() * 28) + 1); // Random day in current month
      eventDate.setHours(8 + Math.floor(Math.random() * 10), Math.floor(Math.random() * 60), 0); // Random time between 8 AM and 6 PM
      
      sampleEvents.push({
        id: `other-${i}`,
        title: `Sonstiger Termin ${i + 1}`,
        date: eventDate,
        type: 'other',
        description: 'Sonstiger Termin',
        color: 'purple'
      });
    }
    
    // Add an event for today
    const todayEvent: CalendarEvent = {
      id: 'today-event',
      title: 'Heutiger Termin',
      date: new Date(today.setHours(14, 0, 0, 0)),
      type: 'meeting',
      description: 'Wichtiger Termin für heute',
      location: 'Konferenzraum',
      participants: ['Max Mustermann', 'Anna Schmidt'],
      color: 'red'
    };
    
    sampleEvents.push(todayEvent);
    
    // Add an event for tomorrow
    const tomorrowEvent: CalendarEvent = {
      id: 'tomorrow-event',
      title: 'Morgiger Termin',
      date: new Date(addDays(today, 1).setHours(10, 30, 0, 0)),
      type: 'installation',
      description: 'Installation für morgen',
      location: 'Baustelle',
      participants: ['Max Mustermann'],
      color: 'blue'
    };
    
    sampleEvents.push(tomorrowEvent);
    
    return sampleEvents;
  }

  return (
    <div className="min-h-screen bg-gray-50 p-8">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="mb-8">
          <button
            onClick={() => navigate(-1)}
            className="flex items-center text-gray-600 hover:text-gray-900 mb-4"
          >
            <ArrowLeft className="h-5 w-5 mr-1" />
            <span>Zurück</span>
          </button>
          
          <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
            <h1 className="text-2xl font-bold text-gray-900">Kalender</h1>
            <div className="flex flex-wrap gap-2">
              <button
                onClick={() => setShowNewEventForm(true)}
                className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
              >
                <Plus className="w-5 h-5" />
                <span>Neuer Termin</span>
              </button>
              
              <button
                onClick={() => setShowFilters(!showFilters)}
                className="flex items-center gap-2 px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors"
              >
                <Filter className="w-5 h-5" />
                <span>Filter</span>
              </button>
              
              <button
                onClick={handleExportEvents}
                className="flex items-center gap-2 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors"
              >
                <Download className="w-5 h-5" />
                <span>Exportieren</span>
              </button>
            </div>
          </div>
        </div>
        
        {/* Search and Filters */}
        <div className="mb-6">
          <div className="relative">
            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
              <Search className="h-5 w-5 text-gray-400" />
            </div>
            <input
              type="text"
              placeholder="Termine suchen..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-10 pr-4 py-2 w-full rounded-lg border border-gray-300 shadow-sm focus:ring-blue-500 focus:border-blue-500"
            />
          </div>
          
          {showFilters && (
            <div className="mt-4 bg-white p-4 rounded-lg shadow border border-gray-200">
              <div className="flex justify-between items-center mb-4">
                <h3 className="font-medium text-gray-700">Filter</h3>
                <button
                  onClick={resetFilters}
                  className="text-sm text-blue-600 hover:text-blue-800"
                >
                  Zurücksetzen
                </button>
              </div>
              
              <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Typ
                  </label>
                  <select
                    value={filters.type}
                    onChange={(e) => setFilters({...filters, type: e.target.value as '' | CalendarEvent['type']})}
                    className="w-full p-2 border border-gray-300 rounded-lg"
                  >
                    <option value="">Alle Typen</option>
                    <option value="installation">Installation</option>
                    <option value="meeting">Meeting</option>
                    <option value="other">Sonstiges</option>
                  </select>
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Von Datum
                  </label>
                  <input
                    type="date"
                    value={filters.startDate}
                    onChange={(e) => setFilters({...filters, startDate: e.target.value})}
                    className="w-full p-2 border border-gray-300 rounded-lg"
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Bis Datum
                  </label>
                  <input
                    type="date"
                    value={filters.endDate}
                    onChange={(e) => setFilters({...filters, endDate: e.target.value})}
                    className="w-full p-2 border border-gray-300 rounded-lg"
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Ort
                  </label>
                  <input
                    type="text"
                    value={filters.location}
                    onChange={(e) => setFilters({...filters, location: e.target.value})}
                    placeholder="Ort eingeben..."
                    className="w-full p-2 border border-gray-300 rounded-lg"
                  />
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Calendar Container */}
        <div className="bg-white rounded-lg shadow-lg overflow-hidden">
          <Calendar
            events={filteredEvents}
            onDateSelect={handleDateSelect}
            onEventClick={handleEventClick}
            onAddEvent={handleDateSelect}
          />
        </div>
        
        {/* Event count summary */}
        <div className="mt-4 bg-white p-4 rounded-lg shadow border border-gray-200">
          <div className="flex flex-wrap gap-4">
            <div className="flex items-center gap-2">
              <div className="w-3 h-3 rounded-full bg-blue-500"></div>
              <span className="text-sm text-gray-700">
                {filteredEvents.filter(e => e.type === 'installation').length} Installationen
              </span>
            </div>
            <div className="flex items-center gap-2">
              <div className="w-3 h-3 rounded-full bg-green-500"></div>
              <span className="text-sm text-gray-700">
                {filteredEvents.filter(e => e.type === 'meeting').length} Meetings
              </span>
            </div>
            <div className="flex items-center gap-2">
              <div className="w-3 h-3 rounded-full bg-purple-500"></div>
              <span className="text-sm text-gray-700">
                {filteredEvents.filter(e => e.type === 'other').length} Sonstige
              </span>
            </div>
            <div className="flex items-center gap-2 ml-auto">
              <span className="text-sm font-medium text-gray-700">
                Gesamt: {filteredEvents.length} Termine
              </span>
            </div>
          </div>
        </div>

        {/* New Event Form Modal */}
        {showNewEventForm && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
            <div className="bg-white rounded-lg shadow-xl w-full max-w-lg max-h-[90vh] flex flex-col">
              <div className="flex justify-between items-center px-6 py-4 border-b border-gray-200">
                <h2 className="text-xl font-semibold text-gray-800 flex items-center gap-2">
                  <CalendarIcon className="w-6 h-6 text-blue-600" />
                  <span>Neuer Termin</span>
                </h2>
                <button
                  onClick={() => setShowNewEventForm(false)}
                  className="text-gray-500 hover:text-gray-700"
                >
                  <X className="w-6 h-6" />
                </button>
              </div>

              <div className="overflow-y-auto flex-1 px-6 py-4">
                <form onSubmit={handleCreateEvent} className="space-y-6">
                  {error && (
                    <div className="rounded-md bg-red-50 p-4">
                      <div className="flex">
                        <div className="flex-shrink-0">
                          <AlertCircle className="h-5 w-5 text-red-400" />
                        </div>
                        <div className="ml-3">
                          <p className="text-sm text-red-700">{error}</p>
                        </div>
                      </div>
                    </div>
                  )}

                  {success && (
                    <div className="rounded-md bg-green-50 p-4">
                      <div className="flex">
                        <div className="flex-shrink-0">
                          <Check className="h-5 w-5 text-green-400" />
                        </div>
                        <div className="ml-3">
                          <p className="text-sm text-green-700">{success}</p>
                        </div>
                      </div>
                    </div>
                  )}

                  <div className="space-y-6">
                    <div>
                      <label htmlFor="title" className="block text-sm font-medium text-gray-700 mb-1">
                        Titel
                      </label>
                      <input
                        id="title"
                        type="text"
                        value={newEvent.title}
                        onChange={(e) => setNewEvent(prev => ({ ...prev, title: e.target.value }))}
                        className="appearance-none block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm placeholder-gray-400 focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                        placeholder="z.B. Installation WE 101"
                        required
                      />
                    </div>

                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <label htmlFor="date" className="block text-sm font-medium text-gray-700 mb-1">
                          Datum
                        </label>
                        <input
                          id="date"
                          type="date"
                          value={newEvent.date}
                          onChange={(e) => setNewEvent(prev => ({ ...prev, date: e.target.value }))}
                          className="appearance-none block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                          required
                        />
                      </div>
                      <div>
                        <label htmlFor="time" className="block text-sm font-medium text-gray-700 mb-1">
                          Uhrzeit
                        </label>
                        <input
                          id="time"
                          type="time"
                          value={newEvent.time}
                          onChange={(e) => setNewEvent(prev => ({ ...prev, time: e.target.value }))}
                          className="appearance-none block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                          required
                        />
                      </div>
                    </div>

                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <label htmlFor="type" className="block text-sm font-medium text-gray-700 mb-1">
                          Typ
                        </label>
                        <select
                          id="type"
                          value={newEvent.type}
                          onChange={(e) => setNewEvent(prev => ({ ...prev, type: e.target.value as CalendarEvent['type'] }))}
                          className="appearance-none block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                        >
                          <option value="installation">Installation</option>
                          <option value="meeting">Meeting</option>
                          <option value="other">Sonstiges</option>
                        </select>
                      </div>

                      <div>
                        <label htmlFor="color" className="block text-sm font-medium text-gray-700 mb-1 flex items-center gap-1">
                          <Palette className="w-4 h-4" />
                          Farbe
                        </label>
                        <select
                          id="color"
                          value={newEvent.color}
                          onChange={(e) => setNewEvent(prev => ({ ...prev, color: e.target.value as EventColor }))}
                          className="appearance-none block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                        >
                          {Object.entries(eventColors).map(([value, config]) => (
                            <option key={value} value={value}>
                              {config.name}
                            </option>
                          ))}
                        </select>
                      </div>
                    </div>

                    <div>
                      <label htmlFor="location" className="block text-sm font-medium text-gray-700 mb-1">
                        Ort
                      </label>
                      <input
                        id="location"
                        type="text"
                        value={newEvent.location}
                        onChange={(e) => setNewEvent(prev => ({ ...prev, location: e.target.value }))}
                        className="appearance-none block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm placeholder-gray-400 focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                        placeholder="z.B. Musterstraße 1, 12345 Berlin"
                      />
                    </div>

                    <div>
                      <label htmlFor="participants" className="block text-sm font-medium text-gray-700 mb-1">
                        Teilnehmer (durch Komma getrennt)
                      </label>
                      <input
                        id="participants"
                        type="text"
                        value={newEvent.participants}
                        onChange={(e) => setNewEvent(prev => ({ ...prev, participants: e.target.value }))}
                        className="appearance-none block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm placeholder-gray-400 focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                        placeholder="z.B. Max Mustermann, Anna Schmidt"
                      />
                    </div>

                    <div>
                      <label htmlFor="description" className="block text-sm font-medium text-gray-700 mb-1">
                        Beschreibung
                      </label>
                      <textarea
                        id="description"
                        value={newEvent.description}
                        onChange={(e) => setNewEvent(prev => ({ ...prev, description: e.target.value }))}
                        className="appearance-none block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm placeholder-gray-400 focus:outline-none focus:ring-blue-500 focus:border-blue-500 h-24 resize-none"
                        placeholder="Zusätzliche Details zum Termin..."
                      />
                    </div>
                  </div>
                </form>
              </div>

              <div className="border-t border-gray-200 px-6 py-4">
                <div className="flex justify-end gap-3">
                  <button
                    type="button"
                    onClick={() => setShowNewEventForm(false)}
                    className="px-4 py-2 text-gray-700 hover:bg-gray-100 rounded-lg transition-colors"
                  >
                    Abbrechen
                  </button>
                  <button
                    onClick={handleCreateEvent}
                    disabled={isCreatingEvent}
                    className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:bg-blue-300"
                  >
                    {isCreatingEvent ? (
                      <>
                        <Loader className="w-5 h-5 animate-spin" />
                        <span>Wird erstellt...</span>
                      </>
                    ) : (
                      <>
                        <Plus className="w-5 h-5" />
                        <span>Termin erstellen</span>
                      </>
                    )}
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Event Details Modal */}
        {selectedEvent && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
            <div className="bg-white rounded-lg shadow-xl w-full max-w-lg">
              {/* Header */}
              <div className="flex justify-between items-center p-6 border-b">
                <div className="flex items-center gap-3">
                  <div className={`p-2 rounded-lg ${eventColors[selectedEvent.color || 'blue'].bg}`}>
                    <CalendarIcon className={`w-6 h-6 ${eventColors[selectedEvent.color || 'blue'].text}`} />
                  </div>
                  <div>
                    <h3 className="text-lg font-semibold text-gray-900">
                      {selectedEvent.title}
                    </h3>
                    <p className="text-sm text-gray-500">
                      {format(selectedEvent.date, 'EEEE, d. MMMM yyyy', { locale: de })}
                    </p>
                  </div>
                </div>
                <button
                  onClick={() => setSelectedEvent(null)}
                  className="text-gray-400 hover:text-gray-500"
                >
                  <X className="w-6 h-6" />
                </button>
              </div>

              {/* Content */}
              <div className="p-6">
                {/* Time */}
                <div className="flex items-start gap-3">
                  <Clock className="w-5 h-5 text-gray-400 mt-0.5" />
                  <div>
                    <p className="text-sm font-medium text-gray-900">Uhrzeit</p>
                    <p className="text-sm text-gray-500">
                      {format(selectedEvent.date, 'HH:mm', { locale: de })} Uhr
                    </p>
                  </div>
                </div>

                {/* Location */}
                {selectedEvent.location && (
                  <div className="flex items-start gap-3 mt-4">
                    <MapPin className="w-5 h-5 text-gray-400 mt-0.5" />
                    <div>
                      <p className="text-sm font-medium text-gray-900">Ort</p>
                      <p className="text-sm text-gray-500">{selectedEvent.location}</p>
                    </div>
                  </div>
                )}

                {/* Participants */}
                {selectedEvent.participants && selectedEvent.participants.length > 0 && (
                  <div className="flex items-start gap-3 mt-4">
                    <Users className="w-5 h-5 text-gray-400 mt-0.5" />
                    <div>
                      <p className="text-sm font-medium text-gray-900">Teilnehmer</p>
                      <div className="space-y-1">
                        {selectedEvent.participants.map((participant, index) => (
                          <p key={index} className="text-sm text-gray-500">{participant}</p>
                        ))}
                      </div>
                    </div>
                  </div>
                )}

                {/* Description */}
                {selectedEvent.description && (
                  <div className="mt-4 pt-4 border-t">
                    <p className="text-sm text-gray-500 whitespace-pre-line">
                      {selectedEvent.description}
                    </p>
                  </div>
                )}
              </div>

              {/* Footer */}
              <div className="flex justify-between gap-3 px-6 py-4 bg-gray-50 rounded-b-lg">
                <button
                  onClick={() => handleDeleteEvent(selectedEvent.id)}
                  className="px-4 py-2 text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                >
                  Löschen
                </button>
                
                <div className="flex gap-3">
                  <button
                    onClick={() => setSelectedEvent(null)}
                    className="px-4 py-2 text-gray-700 hover:bg-gray-100 rounded-lg transition-colors"
                  >
                    Schließen
                  </button>
                  {selectedEvent.buildingId && (
                    <button
                      onClick={() => handleNavigateToBuilding(selectedEvent.buildingId, selectedEvent.apartmentId)}
                      className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
                    >
                      Zum Gebäude
                    </button>
                  )}
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};